import { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import styles from './cpPlanningParamsPanel.module.scss';

interface ParamConfig {
  key: string;
  label: string;
  unit: string;
  isPercent?: boolean;
  min: number;
  max: number;
  step: number;
}

const PARAM_DISPLAY: ParamConfig[] = [
  { key: 'downtime_factor', label: 'Downtime Factor', unit: '%', isPercent: true, min: 0, max: 100, step: 1 },
  { key: 'hours_per_shift', label: 'Hrs/Shift (HC)', unit: 'hrs', min: 1, max: 24, step: 0.5 },
  { key: 'hours_per_shift_room', label: 'Hrs/Shift (Room)', unit: 'hrs', min: 1, max: 24, step: 0.5 },
  { key: 'pa_leads', label: 'PA Lead', unit: 'staff', min: 0, max: 20, step: 1 },
  { key: 'mo_leads', label: 'MO Lead', unit: 'staff', min: 0, max: 20, step: 1 },
  { key: 'qi_leads', label: 'QI Lead', unit: 'staff', min: 0, max: 20, step: 1 },
  { key: 'aggregation_staff', label: 'Aggregation', unit: 'staff', min: 0, max: 20, step: 1 },
  { key: 'shift_coverage', label: 'Shift Coverage', unit: 'staff', min: 0, max: 20, step: 1 },
  { key: 'qls', label: 'QLS', unit: 'staff', min: 0, max: 20, step: 1 },
  { key: 'shifts_per_day', label: 'Shifts/Day', unit: 'shifts', min: 1, max: 3, step: 1 },
  { key: 'lookback_months', label: 'CO Lookback', unit: 'months', min: 1, max: 12, step: 1 },
];

const clamp = (val: number, min: number, max: number): number =>
  Math.min(Math.max(val, min), max);

/** Strip characters that are illegal for this param type. */
function sanitize(raw: string, p: ParamConfig): string {
  // Integer params (step === 1, incl. percent display): digits only
  if (p.step === 1 || p.isPercent) {
    return raw.replace(/[^0-9]/g, '');
  }
  // Decimal params: digits + at most one dot
  const stripped = raw.replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : stripped;
}

/** Returns an error message, or null if valid. */
function validate(draft: string, p: ParamConfig): string | null {
  if (draft === '' || draft === '.') return 'Required';
  const n = parseFloat(draft);
  if (isNaN(n)) return 'Must be a number';
  const lo = p.min;
  const hi = p.max;
  if (n < lo) return `Min ${lo}`;
  if (n > hi) return `Max ${hi}`;
  return null;
}

const CPPlanningParamsPanel = observer(() => {
  const store = useCapacityStore();
  const [expanded, setExpanded] = useState(true);
  // serverParams = last-computed params (null for fresh dashboards)
  // effectiveParams = what to initialise the panel with (falls back to store live params)
  const serverParams    = store.dashboardData?.summary?.planning_params ?? null;
  const effectiveParams = serverParams ?? store.planningParams;
  // A brand-new dashboard has no computed data yet — treat Apply as always actionable
  const isFirstRun = store.dashboardData === null;

  const [localParams, setLocalParams] = useState<Record<string, number>>(
    () => ({ ...effectiveParams }),
  );
  const [dirty, setDirty]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Raw string drafts so the user can type freely without the field snapping back
  const [draftStrings, setDraftStrings] = useState<Record<string, string>>(() => {
    const drafts: Record<string, string> = {};
    PARAM_DISPLAY.forEach((p) => {
      const raw = effectiveParams[p.key] ?? 0;
      drafts[p.key] = String(p.isPercent ? Math.round(raw * 100) : raw);
    });
    return drafts;
  });

  const [pickerDate, setPickerDate] = useState<Date>(() => {
    const ym = store.startYearMonth || '2026-04';
    const [yr, mn] = ym.split('-').map(Number);
    return new Date(yr, mn - 1, 1);
  });
  const [pickerEndDate, setPickerEndDate] = useState<Date | null>(null);

  useEffect(() => {
    if (serverParams) {
      setLocalParams({ ...serverParams });
      // Sync draft strings to the new server values
      const drafts: Record<string, string> = {};
      PARAM_DISPLAY.forEach((p) => {
        const raw = serverParams[p.key] ?? 0;
        drafts[p.key] = String(p.isPercent ? Math.round(raw * 100) : raw);
      });
      setDraftStrings(drafts);
      setErrors({});
      setDirty(false);
    }
  }, [serverParams]);

  useEffect(() => {
    const ym = store.startYearMonth;
    if (ym) {
      const [yr, mn] = ym.split('-').map(Number);
      setPickerDate(new Date(yr, mn - 1, 1));
    }
  }, [store.startYearMonth]);

  const setError = useCallback((key: string, msg: string | null) => {
    setErrors((prev) => {
      if (!msg) {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key] === msg) return prev;
      return { ...prev, [key]: msg };
    });
  }, []);

  const handleChange = useCallback((p: ParamConfig, rawInput: string) => {
    const cleaned = sanitize(rawInput, p);
    setDraftStrings((prev) => ({ ...prev, [p.key]: cleaned }));

    const err = validate(cleaned, p);
    setError(p.key, err);

    const parsed = parseFloat(cleaned);
    if (isNaN(parsed)) return;
    const clamped = p.isPercent
      ? clamp(parsed, p.min, p.max) / 100
      : clamp(parsed, p.min, p.max);
    setLocalParams((prev) => ({ ...prev, [p.key]: clamped }));
    setDirty(true);
  }, [setError]);

  const handleBlur = useCallback((p: ParamConfig) => {
    // On blur: if out of range, clamp and snap; if empty, revert to last valid
    const draft = draftStrings[p.key] ?? '';
    const parsed = parseFloat(draft);
    if (isNaN(parsed) || draft === '') {
      // Revert to last valid value
      const raw = localParams[p.key] ?? 0;
      const display = p.isPercent ? String(Math.round(raw * 100)) : String(raw);
      setDraftStrings((prev) => ({ ...prev, [p.key]: display }));
      setError(p.key, null);
      return;
    }
    // Clamp silently on blur
    const clamped = clamp(parsed, p.min, p.max);
    const stored  = p.isPercent ? clamped / 100 : clamped;
    const display = p.isPercent ? String(clamped) : String(clamped);
    setDraftStrings((prev) => ({ ...prev, [p.key]: display }));
    setLocalParams((prev)   => ({ ...prev, [p.key]: stored }));
    setError(p.key, null);
  }, [draftStrings, localParams, setError]);

  const handleReset = useCallback(() => {
    const base = effectiveParams;
    setLocalParams({ ...base });
    const drafts: Record<string, string> = {};
    PARAM_DISPLAY.forEach((p) => {
      const raw = base[p.key] ?? 0;
      drafts[p.key] = String(p.isPercent ? Math.round(raw * 100) : raw);
    });
    setDraftStrings(drafts);
    setErrors({});
    const ym = store.startYearMonth;
    const [yr, mn] = ym.split('-').map(Number);
    setPickerDate(new Date(yr, mn - 1, 1));
    setPickerEndDate(null);
    store.setViewFilter('', '');
    setDirty(false);
  }, [effectiveParams, store.startYearMonth, store]);

  const handleApply = useCallback(async () => {
    setSaving(true);
    try {
      const iso = `${pickerDate.getFullYear()}-${String(pickerDate.getMonth() + 1).padStart(2, '0')}`;
      await store.applyPlanningSettings(iso, localParams);
      const endIso = pickerEndDate
        ? `${pickerEndDate.getFullYear()}-${String(pickerEndDate.getMonth() + 1).padStart(2, '0')}`
        : '';
      store.setViewFilter('', endIso);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  }, [store, localParams, pickerDate, pickerEndDate]);

  return (
    <div className={styles.panel}>
      <div
        className={styles.panelHeader}
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded(!expanded)}
      >
        <h2>
          <i className="pi pi-sliders-h" />
          Planning Parameters
          {dirty && <span className={styles.unsavedBadge}>(unsaved)</span>}
        </h2>
        <i
          className={`pi pi-chevron-down ${styles.toggleIcon} ${!expanded ? styles.collapsed : ''}`}
        />
      </div>

      {expanded && (
        <div className={styles.panelBody}>
          <div className={styles.datePickerRow}>
            <div className={styles.datePickerGroup}>
              <span className={styles.datePickerLabel}>Planning Start Month</span>
              <Calendar
                value={pickerDate}
                onChange={(e) => {
                  if (e.value instanceof Date) {
                    setPickerDate(e.value);
                    setDirty(true);
                  }
                }}
                view="month"
                dateFormat="MM yy"
                showIcon
                readOnlyInput
                style={{ width: '200px' }}
                panelStyle={{ minWidth: '280px' }}
              />
            </div>

            <i className={`pi pi-arrow-right ${styles.datePickerArrow}`} />

            <div className={styles.datePickerGroup}>
              <span className={styles.datePickerLabel}>
                Planning End Month
                {!pickerEndDate && (
                  <span style={{ fontWeight: 400, color: '#94a3b8', textTransform: 'none', letterSpacing: 0, marginLeft: 4 }}>
                    (optional)
                  </span>
                )}
                {pickerEndDate && (
                  <button
                    type="button"
                    onClick={() => { setPickerEndDate(null); setDirty(true); }}
                    style={{
                      marginLeft: 6, background: 'none', border: 'none',
                      cursor: 'pointer', color: '#94a3b8', fontSize: 10,
                      padding: 0, lineHeight: 1, verticalAlign: 'middle',
                    }}
                    title="Clear end month"
                  >
                    <i className="pi pi-times-circle" />
                  </button>
                )}
              </span>
              <Calendar
                value={pickerEndDate}
                onChange={(e) => {
                  const v = e.value;
                  setPickerEndDate(v instanceof Date ? v : null);
                  setDirty(true);
                }}
                view="month"
                dateFormat="MM yy"
                showIcon
                readOnlyInput
                placeholder="All months"
                style={{ width: '200px' }}
                panelStyle={{ minWidth: '280px' }}
              />
            </div>
          </div>
          <div className={styles.paramsGrid}>
            {PARAM_DISPLAY.map((p) => {
              const rawValue = localParams[p.key] ?? 0;
              // For percent: store holds 0-1, display as 0-100
              const displayValue = p.isPercent
                ? Math.round(rawValue * 100)
                : rawValue;

              const draftVal  = draftStrings[p.key] ?? String(displayValue);
              const errorMsg  = errors[p.key];
              const rangeHint = `${p.min}–${p.max}`;
              return (
                <div
                  key={p.key}
                  className={`${styles.paramItem} ${errorMsg ? styles.paramItemError : ''}`}
                >
                  <span className={styles.paramLabel}>{p.label}</span>
                  <div className={styles.inputGroup}>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${styles.paramInput} ${errorMsg ? styles.paramInputError : ''}`}
                      value={draftVal}
                      onChange={(e) => handleChange(p, e.target.value)}
                      onBlur={() => handleBlur(p)}
                      aria-invalid={!!errorMsg}
                    />
                    <span className={styles.paramUnit}>{p.unit}</span>
                  </div>
                  {errorMsg
                    ? <span className={styles.paramError}>{errorMsg}</span>
                    : <span className={styles.paramHint}>{rangeHint}</span>
                  }
                </div>
              );
            })}
          </div>

          <div className={styles.actions}>
            {Object.keys(errors).length > 0 && (
              <span className={styles.errSummary}>
                <i className="pi pi-exclamation-triangle" /> Fix {Object.keys(errors).length} invalid field{Object.keys(errors).length > 1 ? 's' : ''}
              </span>
            )}
            {saving && (
              <span className={styles.saving}>
                <i className="pi pi-spin pi-spinner" /> Recalculating...
              </span>
            )}
            <Button
              label="Reset"
              severity="secondary"
              text
              size="small"
              onClick={handleReset}
              disabled={!dirty || saving}
            />
            <Button
              label={isFirstRun ? 'Generate Dashboard' : 'Apply & Recalculate'}
              icon={isFirstRun ? 'pi pi-play' : 'pi pi-refresh'}
              size="small"
              style={{ background: '#522d6e', borderColor: '#522d6e' }}
              onClick={handleApply}
              disabled={(!dirty && !isFirstRun) || saving || Object.keys(errors).length > 0}
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default CPPlanningParamsPanel;
